import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useSelector } from 'react-redux';

import { Main as Container } from 'Components/Main';
import { Details } from './Details';
import { addPolicy, editPolicy, getPolicyById } from 'services/policies-service';
import { getUsersGroups } from 'services/users-groups-service';
import { useAuthTenant } from 'store/auth';
import { Dialog, PageHeader, Button } from 'Components/ui';

/* ── Styled Components ── */

const PageWrap = styled.div`
  padding-top: 24px;
  padding-bottom: 32px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;


/* ── Component ── */

export const PoliciesForm = () => {
  const { t } = useTranslation(['policy']);
  const { id } = useParams();
  const selectedTenant = useSelector(useAuthTenant);
  const navigate = useNavigate();

  const { data: usersGroups = [] } = useQuery(['getUsersGroups'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getUsersGroups(selectedTenant.slug);
    return result?.data?.length > 0 ? result?.data : [];
  });

  const {
    register,
    formState: { errors },
    control,
    getValues,
    setValue,
    handleSubmit,
  } = useForm({
    defaultValues: {
      name: '',
      classification: '',
      userGroups: [],
      description: '',
      companyKey: selectedTenant.slug,
    },
  });

  // Only the policy-level fields are ever sent — never policyVersions / lastVersion / _id, which the
  // version routes own (resending them risked overwriting concurrent workflow changes).
  const formatForm = data => ({
    name: data.name,
    classification: data.classification,
    description: data.description,
    userGroups: data.userGroups.length
      ? data.userGroups.map(userGroupId => {
          const userGroup = usersGroups.find(g => g._id === userGroupId);
          return userGroup
            ? { _id: userGroup._id, name: userGroup.name, active: userGroup.active, companyKey: userGroup.companyKey }
            : { _id: userGroupId, name: '', active: true, companyKey: selectedTenant.slug };
        })
      : [],
  });

  const mutation = useMutation(
    () => (id
      ? editPolicy(id, formatForm(getValues()))
      // Create additionally names the tenant; an edit's tenant always comes from the stored policy.
      : addPolicy({ ...formatForm(getValues()), companyKey: selectedTenant.slug })),
    {
      onError: error => {
        Dialog.fire({
          title: t('policy:form.errorTitle'),
          html: error.response?.data?.message || error.message,
          icon: 'error',
        });
      },
      onSuccess: async data => {
        // Brief, modal success state BEFORE the layout changes — the policy page's header buttons
        // appear where Save was, so an immediate navigate let a double-click hit them.
        await Dialog.fire({
          icon: 'success',
          title: t('policy:form.successTitle'),
          text: t('policy:form.successText'),
          timer: 1500,
          showConfirmButton: false,
        });
        navigate(`/policies/show/${id || data.data.id}`);
      },
    },
  );

  const handleComplete = () => mutation.mutate();

  const getPolicy = useCallback(async () => {
    if (id) {
      try {
        const resPolicy = await getPolicyById(id);
        // Load only the fields this form edits (see formatForm).
        const { name, classification, description, userGroups } = resPolicy.data;
        setValue('name', name ?? '');
        setValue('classification', classification ?? '');
        setValue('description', description ?? '');
        setValue('userGroups', userGroups?.filter(Boolean).map(g => g._id) || []);
      } catch (error) {
        Dialog.alert({
          title: t('policy:form.errorTitle'),
          message: error.response?.data?.message ?? error.message,
          icon: 'error',
        });
      }
    }
  }, [id, setValue, t]);

  useEffect(() => { getPolicy(); }, [getPolicy]);

  const pageTitle = id ? t('policy:form.titleEdit') : t('policy:form.titleAdd');

  return (
    <Container>
      <PageWrap>
        {/* Page Header */}
        <PageHeader
          title={pageTitle}
          segments={[
            { label: t('common:breadcrumbs.compliance') },
            { label: t('policy:page.title'), route: '/policies' },
            { label: pageTitle },
          ]}
          actions={
            <HeaderActions>
              <Button variant="outline" type="button" onClick={() => navigate('/policies')} sx={{ height: '44px', padding: '0 20px' }}>
                {t('common:buttons.cancel')}
              </Button>
              <Button
                variant="primary"
                type="submit"
                form="policy-form"
                disabled={mutation.isLoading}
                isLoading={mutation.isLoading}
                sx={{ height: '44px', padding: '0 20px' }}
              >
                {t('policy:form.save')}
              </Button>
            </HeaderActions>
          }
        />

        {/* Form Card */}
        <Details
          control={control}
          register={register}
          isLoading={mutation.isLoading}
          errors={errors}
          handleComplete={handleComplete}
          handleSubmit={handleSubmit}
          usersGroups={usersGroups}
        />
      </PageWrap>
    </Container>
  );
};
